import { BaseTicketController } from '../../common/base/ticketController';
import _ from 'lodash';
import { FeatureFlagNames as FeatureNames } from '../../common/featureFlags/featureFlags';
import { ProductType, TicketRequestType } from './ticket.constants';
import { waitForTransferringStateName } from '../../components/ticket/states/waitForTransferring/waitForTransferring.state';
import { confirmStateName } from '../../components/ticket/states/confirm/confirm.state';

export const DOWNLOAD_PAYMENT_PARTNER_OVERVIEW_EVENT =
  'downloadPaymentPartnerOverview';

export class TicketController extends BaseTicketController {
  constructor(
    $scope,
    ticketStateName,
    controllerService,
    progressService,
    ticketService,
    TicketActions,
    TicketRequestType,
    TicketStates,
    ViewStatus,
    ticketSidebarPartial,
    ticketClosePartial,
    ticketIbanArrowsPartial,
    ticketStatusCustomPartial,
    ticketStatusCompletedPartial,
    ticketStatusPendingPartial,
    ticketStatusLockedPartial,
    ticketDetailPartial,
    notificationService,
    receivingListStateName,
    transferringListStateName,
    TicketRoutesReceiving,
    pdfViewerService,
    needsConfirmationService,
  ) {
    'ngInject';
    super({
      $scope,
      ticketStateName,
      controllerService,
      progressService,
      ticketService,
      TicketActions,
      TicketRequestType,
      TicketStates,
      ViewStatus,
      ticketSidebarPartial,
      ticketClosePartial,
      ticketIbanArrowsPartial,
      ticketStatusCustomPartial,
      ticketStatusCompletedPartial,
      ticketStatusPendingPartial,
      ticketStatusLockedPartial,
      ticketDetailPartial,
      notificationService,
      receivingListStateName,
      transferringListStateName,
      TicketRoutesReceiving,
      pdfViewerService,
      needsConfirmationService,
    });
  }

  get isActiveState() {
    return (
      this.currentTicketRoute &&
      this.progressService.isCurrentInteractable(
        this.routes,
        this.ticketRoutes,
        this.ticket,
      )
    );
  }

  get firstTicketRoute() {
    return _.first(this.ticketRoutes);
  }

  get isWaitForTransferringState() {
    const { status } = this.ticket;
    const waitForTransferring = _.find(this.TicketRoutesReceiving, {
      route: waitForTransferringStateName,
    });
    return _.includes(waitForTransferring.states, status);
  }

  get toggleRemindersEnabled() {
    const { requestType } = this.ticket;

    if (this.product === ProductType.DWS) return false;

    return (
      this.isFeatureActive(FeatureNames.FF_TOGGLE_REMINDERS) &&
      requestType === TicketRequestType.Incoming &&
      this.isWaitForTransferringState
    );
  }

  get isConfirmState() {
    return this.$state.is(confirmStateName);
  }

  get showDownloadOfPaymentPartnerOverview() {
    return (
      this.isConfirmState &&
      // it is important that ticket has no view status otherwise cards are not shown and we can not collect data which causes errors
      !this.viewStatus &&
      this.isFeatureActive(
        this.FeatureNames
          .FF_PROVIDE_PAYMENT_PARTNERS_OVERVIEW_DOC_FOR_MANUAL_CHECK,
      )
    );
  }

  get hideHeader() {
    return (
      this.isFeatureActive(
        this.FeatureNames.FF_DISABLE_PROGRESS_HEADER_FOR_INCOMING_REQUEST,
      ) ||
      (this.currentTicketRoute && this.currentTicketRoute.hideHeader)
    );
  }

  get currentTicketRoute() {
    return (
      this.ticketRoutes &&
      _.find(this.ticketRoutes, (route) =>
        this.progressService.getIsCurrent(route.route),
      )
    );
  }

  get actions() {
    return (
      this.isActiveState &&
      this.ticket.actions &&
      _.filter(this.ticket.actions, (action) =>
        _.includes(this.currentTicketRoute.actions, action),
      )
    );
  }

  get showActions() {
    return this.actions && this.actions.length;
  }

  get externalLink() {
    return this.ticket && this.ticketService.getExternalLink(this.ticket);
  }

  get isBankWithEmployeesStatus() {
    let tenant = this.envService.getTenant();
    return (
      tenant === this.custom.santander || tenant === this.custom.volkswagenbank
    );
  }
  get employeeStatus() {
    const { ticket } = this;
    // Show a status when customer is employee (only for Santander)
    if (
      ticket &&
      ticket.isCustomerAnEmployee &&
      this.isBankWithEmployeesStatus
    ) {
      return `isEmployee.${ticket.isCustomerAnEmployee}`;
    }
    return null;
  }

  get canHandleTicket() {
    return (
      this.ticket &&
      this.ticketService.ticketFlowService.canHandleTicket(
        this.ticket,
        this.currentTicketRoute,
        this.routeConditions,
      )
    );
  }

  get canArchiveManually() {
    return (
      this.isFeatureActive(FeatureNames.FF_MANUAL_ARCHIVE_SPECIFIC_TICKET) &&
      !this.ticket.archived &&
      this.ticket.isArchivable
    );
  }

  get canWriteNote() {
    return !this.userService.isCompliance();
  }

  $onInit() {
    super.$onInit();
    this.product = this.$stateParams.product;

    this.cleanupUnbind = this.authService.registerForLogoutCleanup(
      this.releaseTicketByOwner.bind(this),
    );

    this.isRevision = this.userService.isRevision();

    // Display sidebar for all users but data capturing provider
    this.displaySidebar = !this.userService.isDataCapturingProvider();

    // Listen on sidebar toggle event
    if (this.displaySidebar) {
      this.$scope.$on('sidebar.toggleSection', (event, section) => {
        // NOTE: the event is fired before the section has been toggled,
        // therefore the section contains the toggled state before the switch.
        this.isSidebarOpen = !section.isOpen;
      });
    }

    // Display iban arrows only when sidebar is hidden
    // or when the user has the revision role.
    this.displayIbanArrows = !this.displaySidebar || this.isRevision;
  }

  $onDestroy() {
    if (this.cleanupUnbind) {
      this.cleanupUnbind();
    }
    this.releaseTicketByOwner();
  }

  ticketChanged(newAidRequestData) {
    super.ticketChanged(newAidRequestData);

    // TODO Get rid of this block. Create a specific callback for those cases.
    if (this.externalLink) {
      return this.go(
        this.externalLink.route,
        this.ticketService.getStateData(this.ticket),
        { silent: true },
      );
    }
    if (this.ticket) {
      this.setViewStatus();
      this.ticketActions = angular.copy(this.actions);
    }
    this.selectDetailsView();
  }

  selectDetailsView() {
    if (
      this.progressService.isCurrentActive(
        this.routes,
        this.ticketRoutes,
        this.ticket,
      ) ||
      this.wantToEdit()
    ) {
      return;
    }

    if (!this.firstTicketRoute) {
      return this.go('home');
    }

    return this.goToAnotherDetailView(
      this.firstTicketRoute.route,
      this.ticketService.getStateData(this.ticket),
    );
  }

  downloadPaymentPartnerOverview() {
    this.appService.notify(DOWNLOAD_PAYMENT_PARTNER_OVERVIEW_EVENT);
  }

  wantToEdit() {
    return (
      this.$state.current.name === 'app.ticket.finances' &&
      this.$stateParams.edit &&
      this.$stateParams.edit === 'true'
    );
  }

  closeTicket() {
    const { id, requestType } = this.ticket;
    this.ticketService.closeTicket(requestType, id, this.isDWS);
  }

  closed() {
    return this.ticketService.isTicketClosed(this.ticket);
  }

  navigateToActive() {
    const state = this.progressService.getActiveState(
      this.routes,
      this.ticketRoutes,
      this.ticket,
    );
    if (state) {
      return this.navigateToState(state.route);
    }
    this.navigateTo('list');
  }

  navigateToState(state) {
    const ticketId = this.ticket.id;
    this.goToAnotherDetailView(state, { ticketId });
  }

  transitioned() {
    super.transitioned();
    this.ticketActions = angular.copy(this.actions);
  }

  onTicketStatusAction() {
    if (this.viewStatusOptions) {
      const { cta } = this.viewStatusOptions;
      if (angular.isFunction(cta)) {
        return cta();
      }
    }
    // Default action
    this.navigateToActive();
  }

  handleCustomerTypeChange() {
    const { ticket } = this;
    const { id, requestType } = ticket;
    const changedValue = !ticket.isCustomerAnEmployee;

    this.ticketService.changeEmployeeStatus(
      requestType,
      id,
      angular.toJson(changedValue),
    );
  }

  showInformPaymentPartners() {
    const { TicketStates } = this;
    return (
      [
        TicketStates.DOCUMENT_SENDING_STARTED,
        TicketStates.DOCUMENT_SENT,
        TicketStates.DOCUMENT_SENT_VIA_FAX,
        TicketStates.ALL_DOCUMENTS_SENT,
      ].indexOf(this.ticket.status) > -1
    );
  }

  informPaymentPartners() {
    this.ticketService
      .informPaymentPartners(this.ticket.id)
      .busy(this, 'isBusy');
  }

  releaseTicket() {
    const { id, requestType } = this.ticket;
    return this.ticketService.releaseTicket(requestType, id);
  }

  releaseTicketByOwner() {
    const { id, requestType } = this.ticket;
    return this.ticketService.releaseTicket(requestType, id, true);
  }

  releaseTicketOverSidebar() {
    return this.releaseTicket().then(() => {
      const { requestType } = this.ticket;
      if (requestType === this.TicketRequestType.Incoming) {
        return this.go(this.receivingListStateName, { product: this.product });
      }
      this.go(this.transferringListStateName);
    });
  }

  get toggleRemindersIcon() {
    return this.ticket.ticket.remindersPaused ? 'play' : 'pause';
  }

  get toggleRemindersText() {
    return this.ticket.ticket.remindersPaused
      ? 'sidebar.section.resume-reminders.title'
      : 'sidebar.section.pause-reminders.title';
  }

  toggleReminders() {
    this.toggleRemindersLoading = true;
    const { id, remindersPaused } = this.ticket.ticket;
    return this.ticketService
      .toggleRemindersWithRefresh(id, remindersPaused)
      .then((res) => {
        this.notificationService.success(
          `ticket.${remindersPaused ? 'resume' : 'pause'}-reminders.success`,
        );
      })
      .finally(() => {
        this.toggleRemindersLoading = false;
      });
  }

  downloadArchiveManually() {
    const { id } = this.ticket.ticket;
    return this.ticketService
      .downloadArchiveManually(id)
      .then((base64String) => {
        const url = this.pdfViewerService.makeBase64Downloadable(base64String);
        this.pdfViewerService.downloadIt(url, `Archiv-ticket-${id}.pdf`);
        this.archivedPdfDownloaded = true;
      });
  }

  markArchiveManually() {
    _.defaults(this.$scope, {
      confirmTitle: 'ticket.manually-archived.modal.title',
      confirmBody: 'ticket.manually-archived.modal.body',
      confirmDismiss: 'ticket.manually-archived.modal.dismiss',
      confirmOk: 'ticket.manually-archived.modal.confirm',
    });

    this.needsConfirmationService.open({
      onSuccess: () => {
        const { id } = this.ticket.ticket;
        return this.ticketService.markAsArchivedManually(id).then((resp) => {
          this.notificationService.success('ticket.manually-archived.success');
        });
      },
      scope: this.$scope,
    });
  }
}
